/*
 * <heritage-reserve-modal>
 *
 * Premium replacement for the legacy native-alert "reserve" flow.
 * Mounted once in layout/theme.liquid (via sections/reserve-modal.liquid)
 * so a single instance handles every reserve trigger across the site.
 *
 * Two responsibilities:
 *
 *   1. Click interceptor. Catches clicks on any `[data-reserve-trigger]`
 *      anchor on the page, walks up to the nearest `[data-mdj-product]`
 *      ancestor for product context, and dispatches an
 *      `mdj:reserve-open` event on document.
 *
 *      The PDP availability-pill descriptor renders a trigger anchor
 *      via the descriptor_out_for_viewing setting. Other surfaces (the
 *      collection-card overlay, future quick-reserve buttons, etc.)
 *      can use the same `[data-reserve-trigger]` pattern.
 *
 *   2. Modal controller. Listens for `mdj:reserve-open`, populates the
 *      product preview, decides initial state (signin / confirm /
 *      success), and on submit hands the payload to MDJReserves.add().
 *      The MDJReserves store mirrors the payload to the configured
 *      reserve webhook (see assets/heritage-reserve.js), which is what
 *      the MDJ desk's email automation listens to.
 *
 * View states (driven by data-state on the host):
 *   confirm — signed-in partner, ready to submit
 *   signin  — anonymous visitor, gate to login first
 *   success — submission accepted (or piece already reserved)
 *
 * Customer context (name / email / phone / address / company) is read
 * once from window.__MDJ_INTEGRATIONS__.customer (server-rendered in
 * theme.liquid) — no network round-trip on open.
 */

class HeritageReserveModal extends HTMLElement {
  constructor() {
    super();
    this.handleClose = this.handleClose.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleReserveOpen = this.handleReserveOpen.bind(this);
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;

    // Portal to document.body so the fixed-position overlay escapes
    // the parent .shopify-section's stacking context (z-index: 2 in
    // base.css). The header group sits at z-index: 100 — without
    // portaling, the modal renders under the header. Same pattern as
    // <heritage-gallery-lightbox> and <heritage-memo-modal>.
    if (document.body && this.parentNode !== document.body) {
      document.body.appendChild(this);
      return;
    }

    this._initialized = true;

    this.overlayEl   = this.querySelector('[data-reserve-modal-overlay]');
    this.closeEls    = this.querySelectorAll('[data-reserve-modal-close]');
    this.submitBtn   = this.querySelector('[data-reserve-submit]');
    this.imageEl     = this.querySelector('[data-reserve-image]');
    this.eraEl       = this.querySelector('[data-reserve-era]');
    this.nameEl      = this.querySelector('[data-reserve-name]');
    this.metaEl      = this.querySelector('[data-reserve-meta]');
    this.noteEl      = this.querySelector('[data-reserve-note]');
    this.errorEl     = this.querySelector('[data-reserve-error]');
    this.signinLink  = this.querySelector('[data-reserve-signin-link]');

    // Reserve intent — required radio field added so the MDJ desk knows
    // whether the partner is reserving for Memo (10-day viewing on return)
    // or to Purchase outright. Purchase intent on a piece currently out
    // on memo is a strong signal — there's a paying buyer waiting.
    this.intentFieldset = this.querySelector('[data-reserve-intent]');
    this.intentInputs   = this.querySelectorAll('[data-reserve-intent-input]');
    this.intentLabels = {
      memo:     this.dataset.intentMemoLabel || 'For Memo',
      purchase: this.dataset.intentPurchaseLabel || 'To Purchase'
    };
    this.intentErrorMessage = this.dataset.intentErrorMessage || 'Please choose Memo or Purchase before submitting.';

    // Shopify-native contact form — populated + posted on submit so
    // MDJ gets the email through Shopify's built-in mail pipeline.
    this.contactForm     = this.querySelector('#heritage-reserve-contact-form');
    this.contactSubject  = this.querySelector('[data-reserve-contact-subject]');
    this.contactName     = this.querySelector('[data-reserve-contact-name]');
    this.contactEmail    = this.querySelector('[data-reserve-contact-email]');
    this.contactPhone    = this.querySelector('[data-reserve-contact-phone]');
    this.contactBody     = this.querySelector('[data-reserve-contact-body]');

    this.emailTemplates = this.readEmailTemplates();

    this.isSignedIn  = this.dataset.isSignedIn === 'true';
    this.loginUrl    = this.dataset.loginUrl || '/account/login';
    this.reservesUrl = this.dataset.reservesUrl || '/pages/reserves';

    this.currentItem = null;

    this.closeEls.forEach((btn) => btn.addEventListener('click', this.handleClose));
    if (this.overlayEl) this.overlayEl.addEventListener('click', this.handleClose);
    if (this.submitBtn) this.submitBtn.addEventListener('click', this.handleSubmit);

    // Clear the intent error state the moment the partner picks an
    // option, so the red outline disappears alongside the correction
    // (rather than waiting for a re-submit attempt).
    if (this.intentInputs) {
      this.intentInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (this.intentFieldset) this.intentFieldset.dataset.intentError = 'false';
          if (this.errorEl && this.errorEl.textContent === this.intentErrorMessage) {
            this.errorEl.hidden = true;
            this.errorEl.textContent = '';
          }
        });
      });
    }

    document.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('mdj:reserve-open', this.handleReserveOpen);
    document.addEventListener('click', this.handleTriggerClick);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('mdj:reserve-open', this.handleReserveOpen);
    document.removeEventListener('click', this.handleTriggerClick);
  }

  /*
   * Global click interceptor — turns any [data-reserve-trigger] anchor
   * into an mdj:reserve-open event with product context pulled off the
   * nearest [data-mdj-product] ancestor. Allows the descriptor in
   * settings to remain a static HTML string while still resolving the
   * piece-specific payload at click time.
   */
  handleTriggerClick(event) {
    const trigger = event.target.closest && event.target.closest('[data-reserve-trigger]');
    if (!trigger) return;
    event.preventDefault();

    const ctx = trigger.closest('[data-mdj-product]');
    if (!ctx) {
      if (window.console) console.warn('reserve-modal: [data-reserve-trigger] fired without a [data-mdj-product] ancestor');
      return;
    }

    const item = {
      id:     ctx.dataset.productId || '',
      handle: ctx.dataset.productHandle || '',
      title:  ctx.dataset.productTitle || '',
      image:  ctx.dataset.productImage || '',
      era:    ctx.dataset.productEra || '',
      sku:    ctx.dataset.productSku || '',
      url:    ctx.dataset.productUrl || (location.origin + location.pathname)
    };

    document.dispatchEvent(new CustomEvent('mdj:reserve-open', { detail: item }));
  }

  handleReserveOpen(event) {
    const detail = (event && event.detail) || {};
    this.currentItem = detail;

    // Product preview
    if (this.imageEl) this.imageEl.style.backgroundImage = detail.image ? `url('${detail.image}')` : '';
    if (this.eraEl) this.eraEl.textContent = detail.era || '';
    if (this.nameEl) this.nameEl.textContent = detail.title || '';
    if (this.metaEl) {
      // Strip the configured display prefix (idempotent).
      const cleanSku = window.MDJFormatSku ? window.MDJFormatSku(detail.sku || '') : (detail.sku || '');
      const parts = [];
      if (cleanSku) parts.push('No. ' + cleanSku);
      this.metaEl.textContent = parts.join(' · ');
    }

    // Pick initial view
    if (!this.isSignedIn) {
      this.setState('signin');
      // Round-trip the partner back to the PDP after sign-in so the
      // reserve flow stays continuous.
      if (this.signinLink && detail.url) {
        try {
          const u = new URL(this.loginUrl, location.origin);
          u.searchParams.set('checkout_url', detail.url);
          this.signinLink.href = u.pathname + u.search;
        } catch (e) { /* malformed url — fall back to plain login link */ }
      }
    } else if (window.MDJReserves && window.MDJReserves.has(detail.id)) {
      // Already in the partner's reserves list — skip the confirm
      // step. (The standalone reserves page is the place to cancel.)
      this.setState('success');
    } else {
      this.setState('confirm');
      if (this.noteEl) this.noteEl.value = '';
      if (this.errorEl) { this.errorEl.hidden = true; this.errorEl.textContent = ''; }
      if (this.submitBtn) this.submitBtn.dataset.busy = 'false';
      // Reset intent selection on every open so a partner can't
      // accidentally inherit the previous reserve's intent.
      if (this.intentInputs) {
        this.intentInputs.forEach((input) => { input.checked = false; });
      }
      if (this.intentFieldset) this.intentFieldset.dataset.intentError = 'false';
    }

    this.open();
  }

  selectedIntent() {
    if (!this.intentInputs) return '';
    for (let i = 0; i < this.intentInputs.length; i++) {
      if (this.intentInputs[i].checked) return this.intentInputs[i].value || '';
    }
    return '';
  }

  handleSubmit() {
    if (!this.currentItem || !window.MDJReserves) return;

    // Required-field guard: intent (memo vs purchase) must be picked
    // before the reserve can be submitted. We paint the fieldset error
    // state + surface a message in the existing error slot, and bail
    // before any contact-form / store / webhook work happens.
    const intent = this.selectedIntent();
    if (!intent) {
      if (this.intentFieldset) this.intentFieldset.dataset.intentError = 'true';
      if (this.errorEl) {
        this.errorEl.hidden = false;
        this.errorEl.textContent = this.intentErrorMessage;
      }
      if (this.submitBtn) this.submitBtn.dataset.busy = 'false';
      return;
    }
    if (this.intentFieldset) this.intentFieldset.dataset.intentError = 'false';
    if (this.errorEl) { this.errorEl.hidden = true; this.errorEl.textContent = ''; }

    if (this.submitBtn) this.submitBtn.dataset.busy = 'true';

    const cfg = window.__MDJ_INTEGRATIONS__ || {};
    const customer = cfg.customer || {};
    const addr = customer.default_address || {};
    const note = this.noteEl ? (this.noteEl.value || '').trim() : '';
    const now = new Date();

    const intentLabel = this.intentLabels[intent] || intent;

    const context = {
      customer_id: customer.id || null,
      email:       customer.email || '',
      first_name:  customer.first_name || '',
      last_name:   customer.last_name || '',
      phone:       addr.phone || customer.phone || '',
      company:     addr.company || '',
      address: {
        address1:      addr.address1 || '',
        address2:      addr.address2 || '',
        city:          addr.city || '',
        province:      addr.province || '',
        province_code: addr.province_code || '',
        zip:           addr.zip || '',
        country:       addr.country || '',
        country_code:  addr.country_code || ''
      },
      tags:              customer.tags || '',
      note:              note,
      intent:            intent,         // 'memo' | 'purchase' — machine-readable
      intent_label:      intentLabel,    // human-readable label for emails / UI
      requested_at:      now.toISOString(),
      requested_at_local: now.toLocaleString()
    };

    // Tag the stored reserve entry with the intent so the account
    // dashboard card can surface it and the MDJ desk has it on the
    // webhook payload too (MDJReserves.add spreads item into the
    // stored object — see assets/heritage-reserve.js).
    const itemForStore = Object.assign({}, this.currentItem, {
      intent: intent,
      intent_label: intentLabel
    });

    try {
      // Fire MDJ email via Shopify's native contact form first (the
      // visible "did the thing happen" effect). Store write + webhook
      // mirror come after so a store / webhook failure doesn't suppress
      // the email.
      this.submitContactForm(context);
      window.MDJReserves.add(itemForStore, context);
      this.setState('success');
    } catch (err) {
      if (this.errorEl) {
        this.errorEl.hidden = false;
        this.errorEl.textContent = 'Something went wrong. Please call us at the number above to reserve.';
      }
      if (this.submitBtn) this.submitBtn.dataset.busy = 'false';
    }
  }

  /*
   * Submit the hidden Shopify contact form via fetch so the page
   * doesn't navigate. Fire-and-forget — Shopify returns HTML (not
   * JSON), so meaningful error feedback would require parsing the
   * response. Acceptable trade-off: the localStorage add still
   * succeeds, the partner sees the success view, and if the email
   * silently dropped MDJ will hear about it via a follow-up call
   * (worst case) before any harm is done.
   */
  submitContactForm(context) {
    if (!this.contactForm) return;

    const item = this.currentItem || {};
    const tokens = {
      '{partner_name}':    this.firstNonBlank([context.first_name, context.last_name].join(' ').trim(), context.email),
      '{partner_email}':   context.email,
      '{partner_phone}':   context.phone,
      '{partner_company}': context.company,
      '{partner_address}': this.formatAddress(context.address),
      '{product_title}':   item.title,
      '{product_era}':     item.era,
      '{product_sku}':     item.sku,
      '{product_url}':     item.url,
      '{intent}':          context.intent || '',
      '{intent_label}':    context.intent_label || '',
      '{note}':            context.note,
      '{requested_at}':    context.requested_at_local || context.requested_at,
      '{customer_id}':     context.customer_id == null ? '' : String(context.customer_id)
    };

    const subject = this.applyTokens(this.emailTemplates.subject, tokens);
    const body    = this.applyTokens(this.emailTemplates.body, tokens);

    if (this.contactSubject) this.contactSubject.value = subject;
    if (this.contactName)    this.contactName.value    = tokens['{partner_name}'] || '(no name)';
    // Shopify's contact form rejects submissions with a blank email —
    // fall back to the shop's brand email (data-fallback-email, from
    // settings.brand_email) so the message still routes — the body has
    // the partner's actual contact details regardless.
    if (this.contactEmail)   this.contactEmail.value   = context.email || this.dataset.fallbackEmail || '';
    if (this.contactPhone)   this.contactPhone.value   = tokens['{partner_phone}'] || '';
    if (this.contactBody)    this.contactBody.value    = body;

    try {
      fetch(this.contactForm.action, {
        method: 'POST',
        body: new FormData(this.contactForm),
        keepalive: true
      }).catch(() => { /* fire-and-forget */ });
    } catch (e) { /* fetch unavailable — degrade silently */ }
  }

  readEmailTemplates() {
    const el = this.querySelector('[data-reserve-email-templates]');
    const fallback = {
      subject: 'Reserve Request — {product_title}',
      body: 'Partner: {partner_name}\nEmail: {partner_email}\nPhone: {partner_phone}\n\nReserving: {product_title}\nLink: {product_url}\n\nNotes: {note}\n\nRequested: {requested_at}'
    };
    if (!el) return fallback;
    try {
      const parsed = JSON.parse(el.textContent || '{}');
      return {
        subject: parsed.subject || fallback.subject,
        body: parsed.body || fallback.body
      };
    } catch (e) {
      return fallback;
    }
  }

  applyTokens(template, tokens) {
    if (!template) return '';
    let out = template;
    Object.keys(tokens).forEach((key) => {
      const value = tokens[key];
      const safe = (value == null || value === '') ? '—' : String(value);
      // Global replace without regex escaping — token keys are
      // hard-coded curly strings, no user input ever lands here.
      out = out.split(key).join(safe);
    });
    return out;
  }

  formatAddress(addr) {
    if (!addr) return '';
    const line1 = [addr.address1, addr.address2].filter(Boolean).join(' ');
    const cityRegion = [addr.city, addr.province_code || addr.province].filter(Boolean).join(', ');
    const tail = [cityRegion, addr.zip].filter(Boolean).join(' ');
    const country = addr.country_code || addr.country || '';
    return [line1, tail, country].filter(Boolean).join(', ');
  }

  firstNonBlank() {
    for (let i = 0; i < arguments.length; i++) {
      const v = arguments[i];
      if (v != null && String(v).trim() !== '') return v;
    }
    return '';
  }

  setState(name) {
    this.dataset.state = name;
  }

  open() {
    this.dataset.open = 'true';
    this.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('heritage-overlay-open');
  }

  close() {
    this.dataset.open = 'false';
    this.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('heritage-overlay-open');
  }

  handleClose(event) {
    if (event) event.preventDefault();
    this.close();
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.dataset.open === 'true') this.close();
  }
}

if (!customElements.get('heritage-reserve-modal')) {
  customElements.define('heritage-reserve-modal', HeritageReserveModal);
}
